import { useEffect, useCallback, useRef } from 'react';

/**
 * Session security configuration
 */
const SESSION_CONFIG = {
  // 4 hours inactivity timeout
  INACTIVITY_TIMEOUT: 4 * 60 * 60 * 1000,
  // 2 hours absolute session timeout
  ABSOLUTE_TIMEOUT: 2 * 60 * 60 * 1000,
};

interface SessionState {
  lastActivity: number;
  sessionStart: number;
}

/**
 * Silent session timeout hook
 * - Monitors user activity in background
 * - Auto-logs out on inactivity (no modal/warning)
 * - Does NOT show any UI warnings
 */
export function useSessionTimeout(enabled: boolean = true) {
  const stateRef = useRef<SessionState>({
    lastActivity: Date.now(),
    sessionStart: Date.now(),
  });

  const inactivityRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const absoluteRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const expire = useCallback(() => {
    console.info('[Security] Session expired - auto-logout');
    window.dispatchEvent(new CustomEvent('session:expired'));
  }, []);

  const resetInactivity = useCallback(() => {
    if (inactivityRef.current) clearTimeout(inactivityRef.current);
    stateRef.current.lastActivity = Date.now();
    inactivityRef.current = setTimeout(expire, SESSION_CONFIG.INACTIVITY_TIMEOUT);
  }, [expire]);

  const handleActivity = useCallback(() => {
    resetInactivity();
  }, [resetInactivity]);

  useEffect(() => {
    if (!enabled) {
      if (inactivityRef.current) clearTimeout(inactivityRef.current);
      if (absoluteRef.current) clearTimeout(absoluteRef.current);
      return;
    }

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    resetInactivity();

    // Absolute timeout: fire regardless of activity
    absoluteRef.current = setTimeout(expire, SESSION_CONFIG.ABSOLUTE_TIMEOUT);

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
      if (inactivityRef.current) clearTimeout(inactivityRef.current);
      if (absoluteRef.current) clearTimeout(absoluteRef.current);
    };
  }, [enabled, handleActivity, resetInactivity, expire]);
}
